<template>
  <div class="booking-group-dialog">
    <v-card class="booking-group-dialog-card flex-column" tile :loading="isLoading ? 'secondary' : false">
      <v-toolbar
        style="height: auto;"
        elevation="0"
        class="py-3 toolbar-with-subheader flex-grow-0"
        color="white"
      >
        <div class="toolbar-titles-container overflow-hidden">
          <template v-if="isLoading">
            <v-toolbar-title class="pl-5 mt-1">
              <v-skeleton-loader
                type="heading"
                style="width: 200px; max-width: 200px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
            <v-toolbar-title class="toolbar-subheader pl-5 mt-2">
              <v-skeleton-loader
                type="text"
                style="width: 120px; max-width: 120px;"
              ></v-skeleton-loader>
            </v-toolbar-title>
          </template>
          <template v-else>
            <v-toolbar-title class="pl-5 d-flex">
              {{ title }}
              <copy-to-clipboard-button
                :text="bookingGroup?.booking_number"
                class="ml-2"
              ></copy-to-clipboard-button>
            </v-toolbar-title>
            <v-toolbar-title class="pl-5 toolbar-subheader">
              {{ subtitle }}
            </v-toolbar-title>
          </template>
        </div>
        <v-spacer></v-spacer>
        <simple-tooltip tooltip="Redigera gruppbokning">
          <v-btn
            @click="editBooking"
            icon
            :disabled="isLoading || true"
            class="mr-2"
          >
            <v-icon>mdi-pen</v-icon>
          </v-btn>
        </simple-tooltip>
        <v-menu offset-x>
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              icon
              v-bind="attrs"
              v-on="on"
              class="ml-auto my-auto"
            >
              <v-icon>
                mdi-dots-vertical
              </v-icon>
            </v-btn>
          </template>
          <v-card>
            <v-list>
              <v-list-item>
                <v-btn
                  @click="showAddBookingToGroupDialog"
                  block
                  text
                  color="primary"
                >
                  Lägg till existerande bokning i gruppen
                </v-btn>
              </v-list-item>
            </v-list>
          </v-card>
        </v-menu>
        <v-btn icon v-if="isMobile" @click="close">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <v-tabs
        v-model="tabIndex"
        color="secondary"
        show-arrows
        grow
      >
        <v-tabs-slider color="secondary"></v-tabs-slider>
        <v-tab href="#booking-group-information">
          <v-icon v-if="isMobile">mdi-calendar-multiple</v-icon>
          <span v-else>Bokningsinformation</span>
        </v-tab>
        <v-tab href="#bookings">
          <v-icon v-if="isMobile">mdi-book-marker-outline</v-icon>
          <span v-else>Bokningar</span>
        </v-tab>
        <v-tab href="#booking-history">
          <v-icon v-if="isMobile">mdi-book-clock-outline</v-icon>
          <span v-else>Historik</span>
        </v-tab>
        <v-tab href="#charges">
          <v-icon v-if="isMobile">mdi-cash</v-icon>
          <span v-else>Avgifter</span>
        </v-tab>
        <v-tab href="#invoices-receipts">
          <v-badge
            :value="receiptsAndInvoicesCount !== ''"
            color="secondary"
            :content="receiptsAndInvoicesCount"
            offset-x="-2"
            offset-y="8"
          >
            <v-icon v-if="isMobile">mdi-receipt-text</v-icon>
            <span v-else>Kvitton och fakturor</span>
          </v-badge>
        </v-tab>
        <v-tab href="#emails">
          <v-badge
            :value="emailsCount !== ''"
            color="secondary"
            :content="emailsCount"
            offset-x="-2"
            offset-y="8"
          >
            <v-icon v-if="isMobile">mdi-email</v-icon>
            <span v-else>Email</span>
          </v-badge>
        </v-tab>
      </v-tabs>

      <v-divider></v-divider>

      <v-slide-y-transition leave-absolute>
        <v-tabs
          v-show="tabIndex === 'charges'"
          v-model="subTabIndexCharges"
          centered
          slider-color="secondary"
        >
          <v-tab
            href="#accommodation-charges"
            class="px-8"
          >
            Platsavgifter
          </v-tab>
          <v-tab
            href="#other-charges"
            class="px-8"
          >
            <v-badge
              :value="chargesCount !== ''"
              color="secondary"
              :content="chargesCount"
              offset-x="-2"
              offset-y="8"
            >
              Andra avgifter
            </v-badge>
          </v-tab>
        </v-tabs>
      </v-slide-y-transition>

      <v-slide-y-transition leave-absolute>
        <v-tabs
          v-show="tabIndex === 'invoices-receipts'"
          v-model="subTabIndexInvoicesReceipts"
          centered
          slider-color="secondary"
        >
          <v-tab
            href="#receipts"
            class="px-8"
          >
            <v-badge
              :value="receiptCount !== ''"
              color="secondary"
              :content="receiptCount"
              offset-x="-2"
              offset-y="8"
            >
              Kvitton
            </v-badge>
          </v-tab>
          <v-tab
            href="#invoices"
            class="px-8"
          >
            <v-badge
              :value="invoiceCount !== ''"
              color="secondary"
              :content="invoiceCount"
              offset-x="-2"
              offset-y="8"
            >
              Fakturor
            </v-badge>
          </v-tab>
          <v-tab
            href="#payments"
            class="px-8"
          >
            <v-badge
              :value="paymentsCount !== ''"
              color="secondary"
              :content="paymentsCount"
              offset-x="-2"
              offset-y="8"
            >
              Betalningar
            </v-badge>
          </v-tab>
        </v-tabs>
      </v-slide-y-transition>

      <v-divider v-show="tabIndex === 'invoices-receipts' || tabIndex === 'charges'"></v-divider>

      <v-tabs-items v-model="tabIndex">
        <v-tab-item value="booking-group-information">
          <v-sheet class="overflow-y-auto pa-3 full-height">
            <v-row class="ma-0 full-height">
              <v-col class="pb-0 full-height">
                <booking-group-data-list
                  :booking-group="bookingGroup"
                  :is-loading="isLoading"
                  :hidden-sections="['booking-group']"
                  customer-clickable
                  inside-dialog
                ></booking-group-data-list>
              </v-col>
              <v-col class="pb-0 full-height">
                <booking-input-remarks
                  class="pt-4"
                  :is-open="isOpen"
                  soft-border
                  :is-skeleton-loader="isLoading"
                  :booking-remarks="bookingGroup?.remarks"
                  :booking-group-id="bookingGroup?.id"
                  @remarks-updated="remarksUpdated"
                ></booking-input-remarks>
              </v-col>
            </v-row>
          </v-sheet>
        </v-tab-item>
        <v-tab-item value="bookings">
          <v-sheet class="overflow-y-auto pa-3 full-height">
            <bookings-list
              :bookings="bookingGroup?.bookings"
              :loading="isLoading"
              @booking-clicked="bookingClicked"
              @close="close"
              :group-by="null"
              display-customer
            ></bookings-list>
          </v-sheet>
        </v-tab-item>
        <v-tab-item value="booking-history">
          <v-sheet class="overflow-y-auto pa-3 full-height">
            <editing-record-timeline
              :editing-records="editingRecords"
              :loading="fetchingChunks.includes('editing-records')"
            ></editing-record-timeline>
          </v-sheet>
        </v-tab-item>
        <v-tab-item value="charges">
          <v-tabs-items v-model="subTabIndexCharges">
            <v-tab-item value="accommodation-charges">
              <v-sheet class="overflow-y-auto pa-3 full-height">
                <bookings-accommodation-sums
                  :bookings-accommodation-sums="bookingsAccommodationSums"
                  :accommodation-sums="accommodationSums"
                  :bookings="bookingGroup?.bookings ?? []"
                  :booking-group="bookingGroup"
                  :loading="fetchingChunks.includes('accommodation-sums') || fetchingChunks.includes('booking-group')"
                ></bookings-accommodation-sums>
              </v-sheet>
            </v-tab-item>
            <v-tab-item value="other-charges">
              <v-sheet class="overflow-y-auto pa-3 full-height">
                <charges-table
                  :charges="charges"
                  group-by-booking
                  :bookings="bookingGroup?.bookings"
                  :booking-group="bookingGroup"
                  :loading="fetchingChunks.includes('charges') || fetchingChunks.includes('booking-group')"
                  :summary-toggle-enabled="charges?.length > 1"
                  :show-total-row="charges?.length > 1"
                ></charges-table>
              </v-sheet>
            </v-tab-item>
          </v-tabs-items>
        </v-tab-item>
        <v-tab-item value="invoices-receipts">
          <v-tabs-items v-model="subTabIndexInvoicesReceipts">
            <v-tab-item value="receipts">
              <v-sheet class="full-height">
                <receipt-list
                  class="full-height"
                  :receipts="receipts"
                  group-by-booking
                  :bookings="bookingGroup?.bookings"
                  :booking-group="bookingGroup"
                  :show-total-row="receipts.length > 1"
                  flat
                  :loading="fetchingChunks.includes('receipts')"
                  :displayed-columns="['receipt_number', 'total', 'created_at']"
                ></receipt-list>
              </v-sheet>
            </v-tab-item>
            <v-tab-item value="invoices">
              <v-sheet class="full-height">
                <invoice-list
                  class="full-height"
                  :invoices="invoices"
                  group-by-booking
                  :bookings="bookingGroup?.bookings"
                  :booking-group="bookingGroup"
                  :show-total-row="invoices.length > 1"
                  flat
                  :loading="fetchingChunks.includes('invoices')"
                  :displayed-columns="['invoice_number', 'total', 'remaining_amount', 'invoice_date', 'due_date', 'status']"
                ></invoice-list>
              </v-sheet>
            </v-tab-item>
            <v-tab-item value="payments">
              <v-sheet class="full-height">
                <payments-list
                  class="full-height"
                  :receipt-payments="receiptPayments"
                  :invoice-payment-applications="invoicePaymentApplications"
                  :show-total-row="(receiptPayments.length + invoicePaymentApplications.length) > 1"
                  flat
                  :loading="fetchingChunks.includes('payments')"
                  :displayed-columns="['paid_at', 'amount', 'payment_method', 'document']"
                ></payments-list>
              </v-sheet>
            </v-tab-item>
          </v-tabs-items>
        </v-tab-item>
        <v-tab-item value="emails">
          <v-sheet
            class="overflow-y-auto pa-3 full-height"
          >
            <email-timeline
              :emails="emails"
              :booking-group-id="bookingGroup?.id"
              :loading="fetchingChunks.includes('emails')"
              no-emails-message="Inga bekräftelser har skickats på den här bokningen"
            >
              <template v-slot:timeline-actions>
                <v-timeline-item
                  fill-dot
                  color="success"
                >
                  <template v-slot:icon>
                    <v-icon color="white">mdi-email-fast</v-icon>
                  </template>
                  <div class="d-inline-flex flex-column ga-2">
                    <v-btn
                      @click="sendBookingConfirmation"
                      :loading="isSendingBookingConfirmation"
                      color="success"
                      :class="{ 'btn-two-rows': isMobile }"
                    >
                      Skicka ny bokningsbekräftelse
                    </v-btn>
                  </div>
                </v-timeline-item>
              </template>
            </email-timeline>
          </v-sheet>
        </v-tab-item>
      </v-tabs-items>
      <v-card-actions class="booking-dialog-footer full-width">
        <v-spacer></v-spacer>
        <v-btn
          @click="openInRegister"
          :disabled="isLoading"
          color="secondary"
          class="ma-2 ml-auto px-4"
          large
        >
          Öppna i kassan
        </v-btn>
      </v-card-actions>
    </v-card>
    <v-dialog
      v-model="addBookingToGroupDialog.show"
      max-width="500px"
    >
      <v-card class="pa-4 d-flex flex-column">
        <v-alert
          type="info"
          text
        >
          Sök upp en existerande bokning nedan för att lägga till den i denna gruppbokningen. Du kan söka på bland annat gästnamn, platsnamn och bokningsnummer.
        </v-alert>
        <booking-selector
          v-model="addBookingToGroupDialog.selectedBooking"
          search-context="add-booking-to-group"
          :search-param="bookingGroup?.id"
          label="Bokning att lägga till"
          outlined
          autofocus
          hide-details
          class="mt-6"
        ></booking-selector>
        <v-alert
          v-if="addBookingToGroupDialog.errorMessage"
          type="error"
          text
          class="mt-4 mb-0"
        >
          {{ addBookingToGroupDialog.errorMessage }}
        </v-alert>
        <v-btn
          @click="addBookingToGroup"
          color="success"
          class="ml-auto mt-6"
          :disabled="!addBookingToGroupDialog.selectedBooking"
          :loading="addBookingToGroupDialog.isLoading"
        >
          Lägg till bokning
        </v-btn>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { mapState } from 'vuex'

import BookingGroupService from '@/services/BookingGroupService.js'

import BookingGroupDataList from './DataList.vue'
import BookingInputRemarks from '@/components/booking/InputRemarks.vue'
import BookingsList from '@/components/customer/BookingsList.vue'
import EditingRecordTimeline from '@/components/common/EditingRecordTimeline.vue'
import ChargesTable from '@/components/product/ChargesTable.vue'
import BookingsAccommodationSums from './BookingsAccommodationSums.vue'
import InvoiceList from '@/components/invoice/List.vue'
import PaymentsList from '@/components/booking/PaymentsList.vue'
import ReceiptList from '@/components/receipt/List.vue'
import EmailTimeline from '@/components/email/Timeline.vue'
import SimpleTooltip from '@/components/common/SimpleTooltip.vue'
import CopyToClipboardButton from '@/components/common/CopyToClipboardButton.vue'
import BookingSelector from '@/components/booking/BookingSelector.vue'

export default {
  name: 'BookingDialogContent',
  components: {
    BookingGroupDataList,
    BookingInputRemarks,
    BookingsList,
    EditingRecordTimeline,
    ChargesTable,
    BookingsAccommodationSums,
    InvoiceList,
    PaymentsList,
    ReceiptList,
    EmailTimeline,
    SimpleTooltip,
    CopyToClipboardButton,
    BookingSelector
  },
  props: {
    bookingGroup: Object,
    charges: Array,
    bookingsAccommodationSums: Array,
    accommodationSums: Object,
    invoices: Array,
    receipts: Array,
    receiptPayments: Array,
    invoicePaymentApplications: Array,
    emails: Array,
    editingRecords: Array,
    isLoading: Boolean,
    isOpen: Boolean
  },
  data: () => ({
    addBookingToGroupDialog: {
      show: false,
      selectedBooking: null,
      isLoading: false,
      errorMessage: ''
    }
  }),
  computed: {
    ...mapState(
      {
        fetchingChunks: state => state.bookingGroup.dialog.fetchingChunks,
        openTab: state => state.bookingGroup.dialog.openTab,
        isSendingConfirmationBookingGroupIds: state => state.bookingGroup.isSendingConfirmationBookingGroupIds,
        triggerInvoiceUpdated: state => state.invoice.triggerInvoiceUpdated,
        triggerBookingUpdated: state => state.booking.triggerBookingUpdated, // TODO: använda denna för bookinggrupp också, eller egen?
        triggerAccommodationChargesUpdated: state => state.booking.triggerAccommodationChargesUpdated,
        triggerChargesUpdated: state => state.booking.triggerChargesUpdated
      }
    ),
    tabIndex: {
      get () {
        return this.openTab.tabIndex
      },
      set (val) {
        this.$store.commit('bookingGroup/SET_DIALOG_OPEN_TAB', { tabIndex: val })
      }
    },
    subTabIndexCharges: {
      get: function () {
        if (this.subTabIndex !== 'accommodation-charges' && this.subTabIndex !== 'other-charges') {
          return 'accommodation-charges'
        }
        return this.subTabIndex
      },
      set: function (val) {
        if (this.tabIndex === 'charges') {
          this.subTabIndex = val
        }
      }
    },
    subTabIndexInvoicesReceipts: {
      get: function () {
        if (this.subTabIndex !== 'receipts' && this.subTabIndex !== 'invoices' && this.subTabIndex !== 'payments') {
          return this.defaultSubTab
        }
        return this.subTabIndex
      },
      set: function (val) {
        if (this.tabIndex === 'invoices-receipts') {
          this.subTabIndex = val
        }
      }
    },
    subTabIndex: {
      get () {
        return this.openTab.subTabIndex
      },
      set (val) {
        this.$store.commit('bookingGroup/SET_DIALOG_OPEN_TAB', { subTabIndex: val })
      }
    },
    defaultSubTab: function () {
      if (this.invoices.length === 0) {
        return 'receipts'
      }
      if (this.receipts.length === 0) {
        return 'invoices'
      }
      const mostRecentReceipt = this.receipts[0].created_at
      const mostRecentInvoice = this.invoices[0].created_at
      if (mostRecentInvoice.isAfter(mostRecentReceipt)) {
        return 'invoices'
      }
      return 'receipts'
    },
    chargesCount: function () {
      return this.badgeCount(this.charges.length)
    },
    receiptCount: function () {
      return this.badgeCount(this.receipts.length)
    },
    invoiceCount: function () {
      return this.badgeCount(this.invoices.length)
    },
    paymentsCount: function () {
      return this.badgeCount(this.receiptPayments.length + this.invoicePaymentApplications.length)
    },
    receiptsAndInvoicesCount: function () {
      return this.badgeCount((this.receipts?.length ?? 0) + (this.invoices?.length ?? 0))
    },
    emailsCount: function () {
      return this.badgeCount(this.emails.length)
    },
    isMobile: function () {
      return this.$vuetify.breakpoint.smAndDown
    },
    isSendingBookingConfirmation: function () {
      return this.bookingGroup && this.isSendingConfirmationBookingGroupIds.includes(this.bookingGroup.id)
    },
    title: function () {
      return this.bookingGroup && this.bookingGroup.title
    },
    subtitle: function () {
      return this.bookingGroup && this.bookingGroup.subtitle
    }
  },
  methods: {
    badgeCount (count) {
      if (!count) {
        return ''
      }

      return count > 99 ? '99+' : count
    },
    close: function () {
      this.$emit('close')
    },
    openInRegister: function () {
      this.$store.dispatch('closeDialogs')
      this.$router.push({ name: 'PosRegister', params: { chargable: 'booking-group', id: this.bookingGroup.id } })
    },
    showAddBookingToGroupDialog: function () {
      this.addBookingToGroupDialog.selectedBooking = null
      this.addBookingToGroupDialog.errorMessage = ''
      this.addBookingToGroupDialog.show = true
    },
    addBookingToGroup: function () {
      this.addBookingToGroupDialog.errorMessage = ''
      this.addBookingToGroupDialog.isLoading = true

      return BookingGroupService.addBooking({
        bookingGroupId: this.bookingGroup.id,
        bookingId: this.addBookingToGroupDialog.selectedBooking.id
      })
        .then(({ data }) => {
          if (data.status === 'error') {
            this.addBookingToGroupDialog.errorMessage = data.error_message || 'Kunde inte lägga till bokningen i gruppen'
            return
          }

          this.addBookingToGroupDialog.show = false
          this.addBookingToGroupDialog.selectedBooking = null
          this.$store.dispatch('bookingGroup/openDialogById', { bookingGroupId: this.bookingGroup.id, startTab: 'bookings', refresh: true })
        })
        .catch((error) => {
          this.addBookingToGroupDialog.errorMessage = error.response?.data?.error_message || 'Kunde inte lägga till bokningen i gruppen'
        })
        .finally(() => {
          this.addBookingToGroupDialog.isLoading = false
        })
    },
    editBooking: function () {
      // this.$store.dispatch('booking/startEditBooking', this.booking)
    },
    bookingClicked: function (booking) {
      this.$store.dispatch('booking/openDialogById', { bookingId: booking.id })
    },
    remarksUpdated: function ({ bookingGroupId, remarks }) {
      if (this.bookingGroup && this.bookingGroup.id === bookingGroupId) {
        this.$store.commit('bookingGroup/SET_DIALOG_BOOKING_GROUP_REMARKS', remarks)
      }
      if (this.bookingGroupId && this.bookingGroup.id === bookingGroupId && !this.fetchingChunks.includes('editing-records')) {
        this.$store.dispatch('bookingGroup/getBookingGroupEditingRecords', { bookingGroupId: this.bookingGroup.id })
      }
    },
    sendBookingConfirmation: function () {
      this.$store.dispatch('bookingGroup/sendBookingConfirmation', { bookingGroupId: this.bookingGroup.id })
    }
  },
  watch: {
    triggerInvoiceUpdated: function () {
      // variabel som uppdateras efter fakturaändringar
      if (this.isOpen && this.bookingGroup && this.bookingGroup.id) {
        this.$store.dispatch('bookingGroup/getBookingGroupInvoices', { bookingGroupId: this.bookingGroup.id })
        this.$store.dispatch('bookingGroup/getBookingGroupPayments', { bookingGroupId: this.bookingGroup.id })
      }
    },
    triggerBookingUpdated: function () {
      // De flesta booking ändringar leder till nya editingRecords, hämtar om
      if (this.isOpen && this.bookingGroup && this.bookingGroup.id && !this.fetchingChunks.includes('editing-records')) {
        this.$store.dispatch('bookingGroup/getBookingGroupEditingRecords', { bookingGroupId: this.bookingGroup.id })
        this.$store.dispatch('bookingGroup/getBookingGroup', { bookingGroupId: this.bookingGroup.id })
      }
    },
    triggerAccommodationChargesUpdated: function () {
      // Direka ändringar i accommodation charges triggar denna
      if (this.isOpen && this.bookingGroup && this.bookingGroup.id) {
        if (!this.fetchingChunks.includes('editing-records')) {
          this.$store.dispatch('bookingGroup/getBookingGroupEditingRecords', { bookingGroupId: this.bookingGroup.id })
        }
        if (!this.fetchingChunks.includes('accommodation-sums')) {
          this.$store.dispatch('bookingGroup/getBookingGroupAccommodationSums', { bookingGroupId: this.bookingGroup.id })
        }
      }
    },
    triggerChargesUpdated: function () {
      if (this.isOpen && this.bookingGroup && this.bookingGroup.id) {
        this.$store.dispatch('bookingGroup/getBookingGroupCharges', { bookingGroupId: this.bookingGroup.id })
      }
    }
  }
}
</script>
